import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api, { apiErrorMessage } from "@/lib/api";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const STATE = { queued: "Queued", processing: "Processing", completed: "Completed", failed: "Finished with failures", off: "Off" };

function RangeDialog({ open, onCancel, onStart, initial, busy }) {
  const [f, setF] = useState(initial);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setF(initial); }, [open]);
  const bad = !f.from || !f.to || f.to < f.from || f.to > iso(new Date());
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="dl-stamping-dialog">
        <DialogHeader>
          <DialogTitle>Enable AI Photo Stamping</DialogTitle>
          <DialogDescription>AI photo analysis and stamping can be resource-intensive and may take several hours, depending on the number of photos selected. WhatsApp text updates will continue independently.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-slate-500 space-y-1"><span>Start date</span><Input type="date" className="cal-white" value={f.from} max={iso(new Date())} onChange={(e) => setF({ ...f, from: e.target.value })} data-testid="dl-stamping-from" /></label>
          <label className="text-xs text-slate-500 space-y-1"><span>End date</span><Input type="date" className="cal-white" value={f.to} max={iso(new Date())} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="dl-stamping-to" /></label>
        </div>
        {bad && <div className="text-[11px] text-rose-500" data-testid="dl-stamping-range-error">Pick a start date on or before the end date, ending today or earlier.</div>}
        <p className="text-[11px] text-slate-400">Photos already stamped aren't processed again.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="dl-stamping-cancel">Cancel</Button>
          <Button disabled={bad || busy} onClick={() => onStart(f)} className="bg-[#327de3] text-white hover:bg-[#1f64c2]" data-testid="dl-stamping-start">Start AI Stamping</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StampingToggle() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { const { data } = await api.get("/dailylogs/stamping"); setS(data); } catch {} }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  const save = async (body, msg) => {
    setBusy(true);
    try { const { data } = await api.put("/dailylogs/stamping", body); setS(data); setOpen(false); toast.success(msg); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not update AI Photo Stamping")); } finally { setBusy(false); }
  };
  if (!s) return null;
  const initial = { from: s.date_from || daysAgo(2), to: s.date_to || daysAgo(0) };
  return (
    <div className="text-right max-w-[300px]" data-testid="dl-stamping">
      <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-3 pr-1.5 py-1 text-xs font-semibold text-slate-700 cursor-pointer" data-testid="dl-stamping-pill">
        Photo Analysis:
        <span className={`text-[10px] ${s.enabled ? "text-emerald-600" : "text-slate-400"}`} data-testid="dl-stamping-state-label">{s.enabled ? "ON" : "OFF"}</span>
        <Switch checked={s.enabled} disabled={busy} onCheckedChange={(v) => (v ? setOpen(true) : save({ enabled: false }, "AI Photo Stamping turned off; no new photos will be processed"))} data-testid="dl-stamping-switch" />
      </label>
      <p className="text-[11px] text-slate-500 leading-snug mt-1" data-testid="dl-stamping-help">Optional. AI analysis and photo stamping can take hours for large date ranges. Turning this on may delay media processing; WhatsApp text updates will continue independently.</p>
      {s.date_from && (
        <div className="text-[10px] mt-0.5 text-slate-500" data-testid="dl-stamping-status">
          {s.date_from} → {s.date_to} · <b>{STATE[s.state] || s.state}</b>{s.total != null ? ` · ${s.done}/${s.total} done${s.failed ? `, ${s.failed} failed` : ""}` : ""}
          {s.enabled && <button onClick={() => setOpen(true)} className="ml-1 text-[#327de3] font-semibold" data-testid="dl-stamping-edit">Edit range</button>}
          {!s.enabled && s.state === "off" && s.remaining > 0 && <span> · paused (the current small batch may still finish)</span>}
        </div>
      )}
      <RangeDialog open={open} initial={initial} busy={busy} onCancel={() => setOpen(false)}
        onStart={(f) => save({ enabled: true, date_from: f.from, date_to: f.to }, "AI Photo Stamping queued in the background")} />
    </div>
  );
}
